<template>
  <div>
    <component :is="layoutComponent">
      <AdminBar v-if="showAdminBar && isAdmin"/>
      <router-view v-if="shouldRenderShell" />
      <PageSkeleton v-else />
    </component>
  </div>
  <ContentDrawer />
  <Toast/>
  <PwaUpdatePrompt />
  <RouteProgressBar />
  <RouteAnnouncer />

  <PiniaColadaDevtools v-if="isDev" />
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, h, onBeforeUnmount, onErrorCaptured, onMounted, ref, watch } from "vue";
import type { ComponentPublicInstance, FunctionalComponent } from "vue";
import pinia from "@/plugins/PiniaPlugin";

const isDev = import.meta.env.DEV;
const PiniaColadaDevtools = isDev
    ? defineAsyncComponent( () => import( '@pinia/colada-devtools' ).then( m => m.PiniaColadaDevtools ) )
    : () => null;

import PublicLayout from "@/layouts/Public.vue";
import AdminBar from '@/components/layout/AdminBar.vue';
import ContentDrawer from '@/components/modals/ContentDrawer.vue';
import PageSkeleton from '@/components/layout/PageSkeleton.vue';
import PwaUpdatePrompt from '@/components/layout/PwaUpdatePrompt.vue';
import RouteProgressBar from '@/components/layout/RouteProgressBar.vue';
import RouteAnnouncer from '@/components/layout/RouteAnnouncer.vue';
import { loadPrivateLayout, warmPrivateLayout } from "@/layouts/private-layout";
import { lazyView } from "@/utils/lazy-view";
import { afterLoadWhenIdle } from "@/utils/after-load-idle";

// UX roadmap #20: the member shell and the content slide-over were static imports, so every anonymous reader's entry
// chunk carried the header search, the product menu, Vimeo and the newsletter form (zod) without ever rendering them.
//
// The member layout shows its skeleton on the first frame (delay 0): the boot gate (#9) paints the shell at once, and
// a blank frame while this chunk arrives would undo that. The chunk is warmed below and in main.ts, so it is usually
// there already.
const PrivateLayoutSkeleton: FunctionalComponent = () => h( 'div', { class: 'p-4' }, h( PageSkeleton ) );
const PrivateLayout = lazyView( loadPrivateLayout, { loadingComponent: PrivateLayoutSkeleton, delay: 0 } );

import { useApplicationStore } from "@/stores/Application";
import { storeToRefs } from "pinia";
import { usePwaInstall } from "@/composables/pwa-install";
import { useAppDark } from "@/composables/useAppDark";
import { useAppHead } from "@/composables/useAppHead";
import { useRoute } from "vue-router";
import * as tmi from "@/constants";
import routeManager from "@/router/route-manager";

usePwaInstall();
// Held for the app's lifetime so the saved theme follows the member on every route, sign-in included.
useAppDark();
// Rank 49/105: the title template, route titles, robots, canonical and link-preview defaults every page falls back to.
useAppHead();

import type { Options } from 'browser-update';
import { useAuthStore } from "@/stores/Auth";
import { clientStateManager } from "@/services/error-services";
import { capturedErrorContext } from "@/composables/useCapturedErrors";
import { ConfigurationError } from "@tmi/types/errors";

const options: Options = {
  api: 2024.10,
  required: { e: -2, f: -2, o: 0, s: -1, c: -4 },
  insecure: true,
  mobile: true,
  newwindow: true,
  no_permanent_hide: true,
  shift_page_down: false,
  text: {
    msg: 'Your web browser ({brow_name}) is out of date.',
    msgmore: 'This site uses the latest technologies and thus needs a browser that is able to support such features. Update your browser for more security, speed and the best experience on this site.',
    bupdate: 'Update browser',
    bignore: 'Ignore',
    remind: 'You will be reminded in {days} days.'
  },
  test: false,
  dont_show_debuginfo: true
}

// UX roadmap #20: ~26 KB that only an outdated browser needs. It ran synchronously in this setup, from the entry chunk;
// it now loads once the page has loaded and gone idle. A failed download only means no outdated-browser banner.
function runBrowserUpdate(): void {
  import( 'browser-update' )
      .then( ( { default: browserUpdate } ) => browserUpdate( options, options.test ) )
      .catch( () => {} );
}
let cancelBrowserUpdate: () => void = () => {};

const appStore = useApplicationStore( pinia );
const {
  isShellReady,
  isSigningOut
} = storeToRefs( appStore );
const { isAuthenticated } = storeToRefs( useAuthStore() );
const route = useRoute();
const isAuthPage = computed( () =>
  routeManager.isSignInPath( route.path ) ||
  routeManager.isSignInAsUserName( route.name?.toString() ) ||
  routeManager.isSignUpPath( route.path ) ||
  route.path === routeManager.resetPasswordRoute() ||
  route.path === tmi.NewPasswordPath
);
// Public routes render immediately instead of waiting on the whole data layer.
//
// bootstrapShell() awaits nine store initialisations — the ticker universe, every sector, product,
// author, category and tag — and until it settled, EVERY visitor saw a full-screen spinner. That
// included anonymous readers arriving on an article, and search-engine crawlers, none of whom need
// any of it. Components that do need catalog data now show their own loading state in place
// rather than blocking the entire page behind one gate.
//
// Anything hydrating cached content still has to wait for the composition context, because
// hydrateContent() commits a non-reactive snapshot — see the stores' initialize().
//
// UX roadmap #9: that wait now covers only the routed page. The layout (header, sidebar, admin bar)
// renders at once and a PageSkeleton holds the page's place; before, a hard load of any member route
// showed a bare full-screen spinner until the whole data boot had finished, then the shell popped in.
// The shell reads its own state reactively: SidebarMenu waits on isNavigationReady, and the search
// field and notification bell read their stores when used.
const isPublicRoute = computed< boolean >( () => true === route.meta?.public );
// UX roadmap #24: a sign-out drops the token before the redirect home lands, and the layout turns public at once. The
// skeleton holds the page meanwhile, so the private page is not remounted inside the public layout.
// UX roadmap #19: a member page that needs only its own request and the composition catalogs (a trade alert opened
// from SMS or email) opts out with meta.rendersBeforeBoot. The auth guard has already loaded the member's grants, and
// the page's store waits for the catalogs itself, so its request runs beside the boot instead of after it.
const rendersBeforeBoot = computed< boolean >( () => true === route.meta?.rendersBeforeBoot );
const shouldRenderShell = computed( () => !isSigningOut.value
  && ( isShellReady.value || isAuthPage.value || isPublicRoute.value || rendersBeforeBoot.value ) );
const layoutComponent = computed( () => isAuthPage.value ? PublicLayout : ( isAuthenticated.value ? PrivateLayout : PublicLayout ) );
// A member (or someone on the way to signing in) will need the member layout next: fetch it now, not after the redirect.
watch( () => isAuthenticated.value || isAuthPage.value, ( needsMemberLayout ) => {
  if ( needsMemberLayout ) warmPrivateLayout();
}, { immediate: true } );
const isAdmin = computed< boolean >( () => appStore.isAdminSession() );
const showAdminBar = computed< boolean >( () => isAdmin.value || !!( localStorage.getItem( 'is-admin' ) ) );

// The last resort: the layouts' page boundary and the section boundaries (<ErrorBoundary>) report and stop what
// happens under them, so this sees only what escapes them (the header, the sidebar, the content slide-over). It reports
// with the same context they do (UX roadmap #17); it used to queue an empty one, so a report never said where from.
const configurationFault = ref< boolean >( false );
onErrorCaptured( ( err: unknown, instance: ComponentPublicInstance | null, info: string ) => {
  if ( !configurationFault.value ) {
    clientStateManager.queueClientError( err, capturedErrorContext( 'app', instance, info ) );

    // This keeps the browser from throwing multiple errors caused by the root issue of a configuration issue
    if ( err instanceof ConfigurationError ) {
      configurationFault.value = true;
    }
  }

  // Don't allow the error to bubble up any further
  return false;
} );

// lifecycle-1: pagehide, not beforeunload. A cancelled unload, a download or mailto link, or a back/forward-cache
// restore used to leave the sync loop stopped for the rest of the tab session. A page restored from that cache
// catches up (or re-arms its timer) instead.
function onPageShow( event: PageTransitionEvent ): void {
  if ( event.persisted ) {
    appStore.resumeSync();
  }
}

onMounted( () => {
  cancelBrowserUpdate = afterLoadWhenIdle( runBrowserUpdate );
  window.addEventListener( "pagehide", appStore.terminateWorker );
  window.addEventListener( "pageshow", onPageShow );
  window.addEventListener( "unhandledrejection", appStore.handleRejectedPromise );
} );

onBeforeUnmount( () => {
  cancelBrowserUpdate();
  window.removeEventListener( "pagehide", appStore.terminateWorker );
  window.removeEventListener( "pageshow", onPageShow );
  window.removeEventListener( "unhandledrejection", appStore.handleRejectedPromise );
} );

void appStore.bootstrapShell();
</script>

<style lang="scss">
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

// Enter-only and short. This was `all 0.75s` with a 30px slide on both enter and leave, and most of
// its ~30 users are `mode="out-in"`: every view waited 750ms for the old content (or a spinner) to
// slide out, then spent another 750ms sliding in after its data was ready. No leave rule means the
// out-in leave finishes on the next frame.
.slide-down-enter-active {
  transition: opacity 0.15s ease-out;
}

.slide-down-enter-from {
  opacity: 0;
}
</style>
